import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import config from "@/config.js";
import t from "@/services/translationService.js";
import galleryController from "@modules/gallery/galleryController.js";
import tagController from "@modules/tag/tagController.js";
import { viewLink, editButton } from "./rowActions.js";

/**
 * Cockpit → Gallery Manager. Lists every gallery the admin can see (drafts
 * included) with its cover, date, visibility and tags, and opens the existing
 * GalleryEditor overlay for create/edit/delete.
 *
 * The public gallery grid no longer carries admin buttons; this is where
 * galleries are managed. props: { lang }
 */
export default class GalleryManagerPanel extends CockpitPanel {
  async onBeforeMount() {
    await this.load();
  }

  async load() {
    try {
      await galleryController.ensureLoaded();
      this.state.galleries = galleryController.manager.all();
      // Rows list tag *names*, not the keys stored on the gallery — the Cockpit
      // should read like the site does. Tag Manager is where keys are visible.
      this.state.tagLabels = await tagController.labels(this.props.lang);
      this.state.error = "";
    } catch (err) {
      this.state.galleries = [];
      this.state.error = err.message || t.t("gallery.loadError");
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.galleries"));
  }

  lead() {
    return Component.escape(t.t("cockpit.galleries.lead"));
  }

  /**
   * Moving photos gets its own button rather than living inside the gallery
   * editor: it is an action across *two* galleries, so it belongs where both
   * are in reach, not inside a form about one of them. Disabled until there is
   * somewhere to move to — with a single gallery the dialog has no second side.
   */
  actions() {
    const galleries = this.state.galleries || [];
    const canMove = galleries.length > 1;
    return `
      <button type="button" class="btn-secondary" data-action="move" ${canMove ? "" : "disabled"}>${Component.escape(
        t.t("cockpit.galleries.move.open")
      )}</button>
      <button type="button" class="btn-primary" data-action="add">+ ${Component.escape(t.t("cockpit.galleries.add"))}</button>`;
  }

  #visibility(gallery) {
    if (gallery.isPublic()) return { key: "public", label: t.t("cockpit.visibility.public") };
    if (gallery.isDraft()) return { key: "draft", label: t.t("cockpit.visibility.draft") };
    return { key: "private", label: t.t("cockpit.visibility.private") };
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);

    const galleries = this.state.galleries || [];
    if (!galleries.length) return CockpitPanel.empty(t.t("cockpit.galleries.empty"));

    const { lang } = this.props;
    const rows = galleries
      .map((gallery) => {
        const cover = gallery.coverImage();
        const thumb = cover
          ? `<img class="cockpit-row__thumb" src="${config.imageBase}/${encodeURIComponent(gallery.galleryId)}/${encodeURIComponent(cover)}?size=thumb" alt="" loading="lazy" />`
          : `<span class="cockpit-row__thumb cockpit-row__thumb--empty"></span>`;
        const visibility = this.#visibility(gallery);
        const tags = gallery.tagList().length
          ? `<div class="cockpit-row__tags">${gallery
              .tagList()
              .map((tag) => `<span class="cockpit-row__tag">${Component.escape(this.state.tagLabels?.[tag] || tag)}</span>`)
              .join("")}</div>`
          : `<span class="cockpit-row__muted">—</span>`;

        return `
          <tr>
            <td>${thumb}</td>
            <td>
              <span class="cockpit-row__name">${Component.escape(gallery.title(lang))}</span>
              <span class="cockpit-row__muted">${Component.escape(gallery.subtitle(lang))}</span>
            </td>
            <td class="cockpit-row__nowrap">${Component.escape(gallery.date || "—")}</td>
            <td><span class="state-badge state-badge--${visibility.key}">${Component.escape(visibility.label)}</span></td>
            <td>${tags}</td>
            <td class="cockpit-table__actions">
              ${viewLink(`#/gallery/${encodeURIComponent(gallery.galleryId)}`)}
              ${editButton(gallery.galleryId)}
            </td>
          </tr>`;
      })
      .join("");

    return `
      <div class="cockpit-table__wrap">
        <table class="cockpit-table">
          <thead>
            <tr>
              <th class="cockpit-table__thumb-col"></th>
              <th>${Component.escape(t.t("cockpit.galleries.colTitle"))}</th>
              <th>${Component.escape(t.t("cockpit.galleries.colDate"))}</th>
              <th>${Component.escape(t.t("cockpit.galleries.colVisibility"))}</th>
              <th>${Component.escape(t.t("cockpit.galleries.colTags"))}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  onMounted() {
    const add = this.$('[data-action="add"]');
    if (add) this.listen(add, "click", () => this.#edit(null));

    const move = this.$('[data-action="move"]:not([disabled])');
    if (move) this.listen(move, "click", () => galleryController.openMoveImages(() => this.reload(), this.props.lang));

    this.$$("[data-edit]").forEach((btn) =>
      this.listen(btn, "click", () => this.#edit(galleryController.manager.get(btn.getAttribute("data-edit"))))
    );
  }

  #edit(gallery) {
    galleryController.openEditor(gallery, () => this.reload(), this.props.lang);
  }

  async reload() {
    galleryController.invalidate();
    // A gallery save can mint new tags (galleryService calls tagService.ensure),
    // so the vocabulary this panel and the editor read is stale afterwards.
    tagController.invalidate();
    await this.load();
    this.setState({});
  }
}
